import { useSuspenseQuery } from '@tanstack/react-query';

import { useTRPC } from '#src/trpc.js';
import { useActiveDesignContext } from '../context/active-design-context.js';
import { useDrawingManagerContext } from '../context/drawing-manager-context.js';
import { useCadRendererGridSync } from '../sync/cad-renderer-sync-context.js';
import { GridSettingsForm } from './grid-settings-form.js';

import type { ModelGrid } from '@cadstrom/sigma-engine-shared';

export interface GridSettingsProps {
	/** The active design's own grid. */
	modelGrid: ModelGrid;
}

/**
 * Grid settings for the active design, read from and written to the renderer's shared grid state,
 * so every place that renders it shows and edits the same grid.
 *
 * @param props - See {@link GridSettingsProps}.
 * @returns The grid settings form.
 */
export function GridSettings({ modelGrid }: GridSettingsProps) {
	const { activeDesign } = useActiveDesignContext();
	const { drawingManager } = useDrawingManagerContext();
	const { gridSettings, defaultGridSettings, setGridSettings } = useCadRendererGridSync(
		activeDesign.designId,
		activeDesign.modelType,
		modelGrid,
	);
	const trpc = useTRPC();
	const { data: currentUser } = useSuspenseQuery(trpc.user.getCurrent.queryOptions());

	return (
		<GridSettingsForm
			settings={gridSettings}
			defaults={defaultGridSettings}
			isGridSupported={drawingManager?.isGridSupported === true}
			showAppearanceSliders={currentUser.auth.isStaff}
			onChange={setGridSettings}
		/>
	);
}
